import { Col, Form, Row, Space } from 'antd';
import ProductsCard from '../ProductsCard';
import OrderHistories from './OrderHistories';
import ShippingInformation from '../ShippingInformation';
import RefundMethod from './RefundMethod';
import KeyValuePairs from './KeyValuePairs';
import { useTranslation } from 'react-i18next';
import OrderSummary from './OrderSummary';
import CustomerInformation from '../CustomerInformation';
import { useRestContext } from 'components/form/RestInputContext';
import { formatDateTime, formatMoney } from 'utils/formattedString';
import _ from 'lodash';
import Validation from './ButtonActions/Validation';
import { useMemo, useState } from 'react';
import {
  FAILED_QC_REASON,
  RETURN_CHANNEL,
  RETURN_METHOD,
  RETURN_REASON,
  RETURN_SOLUTION,
  RETURN_STATUS,
} from 'constants/ordersReturn';
import ApprovedScreen from './ButtonActions/ApprovedScreen';
import FailedQCScreen from './ButtonActions/FailedQCScreen';
import ReturnAndFailQCTemplate from './ReturnAndFailQCTemplate';
import OrderCurrentStatus from './OrderCurrentStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

export default function FormView() {
  const { t } = useTranslation();
  const { record } = useRestContext();
  const [isLayoutEditProducts, setIsLayoutEditProducts] = useState(false);

  const getCardAction = useMemo(() => {
    switch (record?.status) {
      case RETURN_STATUS['AWAITING_APPROVAL'].value:
        return <Validation />;
      case RETURN_STATUS['APPROVED'].value:
        return <ApprovedScreen />;
      case RETURN_STATUS['FAILED_QC'].value:
        return <FailedQCScreen />;
      default:
        return <></>;
    }
  }, [record?.status]);

  return (
    <div>
      <OrderCurrentStatus
        isLayoutEditProducts={isLayoutEditProducts}
        setIsLayoutEditProducts={setIsLayoutEditProducts}
      />
      <div className="h-[20px]" />
      <OrderSummary />
      <div className="h-[20px]" />
      <Row gutter={[20, 20]}>
        <Col md={15}>
          <Space size={20} direction="vertical" className="w-full">
            <div>
              <ProductsCard
                isEdit
                isLayoutEditProducts={isLayoutEditProducts}
              />
              <Form.Item name="whoPayShippingFee" hidden />
              <Form.Item name="returnMethod" hidden />
            </div>
            <div className="flex gap-[20px]">
              <ReturnAndFailQCTemplate
                cardTitle={t('ordersReturn.returnInformation')}
                data={[
                  {
                    title: t('ordersReturn.reason'),
                    content: t(RETURN_REASON[record?.reason]?.text),
                  },
                  {
                    title: t('ordersReturn.description.label'),
                    content: record?.note,
                  },
                  {
                    title: t('ordersReturn.image'),
                    srcImage: record?.images || [],
                  },
                  // {
                  //    title: t('ordersReturn.solution'),
                  //    content: t(RETURN_SOLUTION[record?.solution]?.text || 'ordersReturn.solutions.returnReplace'),
                  // },
                ]}
              />
              {record?.status === RETURN_STATUS['FAILED_QC']?.value && (
                <ReturnAndFailQCTemplate
                  cardTitle={t('ordersReturn.failQCInformation')}
                  data={[
                    {
                      title: t('ordersReturn.reason'),
                      content: t(
                        FAILED_QC_REASON[record?.failedQCReason]?.label,
                      ),
                    },
                    {
                      title: t('ordersReturn.note'),
                      content: record?.failedQCNote,
                    },
                    {
                      title: t('ordersReturn.image'),
                      srcImage: record?.failedQCImages || [],
                    },
                  ]}
                />
              )}
            </div>
            <OrderHistories />
          </Space>
        </Col>
        <Col md={9}>
          <Space size={20} direction="vertical" className="w-full">
            {!isLayoutEditProducts &&
              record?.order?.statusOms === 'DELIVERY_SUCCESS' &&
              getCardAction}
            <RefundMethod />
            {record?.returnType === RETURN_CHANNEL['OFFLINE'].value &&
              record?.solution === RETURN_SOLUTION['ISSUE_VOUCHER'].value && (
                <KeyValuePairs
                  cardTitle={t('ordersReturn.voucher.title')}
                  data={[
                    {
                      key: t('ordersReturn.voucher.code'),
                      value: record?.voucher || '',
                    },
                    {
                      key: t('ordersReturn.voucher.amount'),
                      value: formatMoney(record?.totalRefund) || 0,
                    },
                  ]}
                />
              )}
            {record?.order?.customer && (
              <CustomerInformation data={record?.order?.customer} />
            )}
            {record?.returnType !== 'OFFLINE' &&
              record?.pickupInformation &&
              record?.returnMethod !== RETURN_METHOD['DROP_OFF'].value && (
                <ShippingInformation
                  hasEdit={
                    record?.status === RETURN_STATUS['AWAITING_APPROVAL'].value
                  }
                  isCallApiEdit
                />
              )}
            <KeyValuePairs
              cardTitle={t('ordersReturn.creator.title')}
              data={[
                {
                  key: t('ordersReturn.creator.createdBy'),
                  value: record?.creator
                    ? `${record?.creator?.firstName || ''} ${record?.creator?.lastName || ''}`
                    : 'Customer',
                },
              ]}
            />
            <KeyValuePairs
              cardTitle={t('ordersReturn.originalOrder.title')}
              data={[
                {
                  key: t('ordersReturn.originalOrder.orderNo'),
                  value: record?.order?.orderNumber,
                  isLink: true,
                  href: `/order-management/${record?.order?.id}/show`,
                },
                {
                  key: t('ordersReturn.originalOrder.orderCreatedTime'),
                  value: formatDateTime(record?.order?.createdAt),
                },
                {
                  key: t('ordersReturn.originalOrder.numberOfItems'),
                  value: _.reduce(
                    record?.order?.items,
                    function (sum, item) {
                      return sum + item?.quantity;
                    },
                    0,
                  ),
                },
                {
                  key: t('ordersReturn.originalOrder.amount'),
                  value: formatMoney(record?.order?.total),
                },
                {
                  key: 'SO',
                  value:
                    record?.order?.saleOrderId ||
                    t('ordersReturn.waitingUpdate'),
                },
                {
                  key: 'SOR',
                  value: record?.sorId || t('ordersReturn.waitingUpdate'),
                },
                {
                  key: 'WH receipt ID',
                  value:
                    record?.warehouseReceiptNo ||
                    t('ordersReturn.waitingUpdate'),
                },
                {
                  key: 'Post WH receipt',
                  value: record?.isPostWR ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    t('ordersReturn.waitingUpdate')
                  ),
                },
              ]}
            />
          </Space>
        </Col>
      </Row>
    </div>
  );
}
